import { DeepMap } from 'react-hook-form';
import {
  JobsiteUpdateInput,
  OnboardingStepKey,
} from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields, titleToKebabCase } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import {
  EditJobsiteConfigurationFormData,
  EditJobsiteConfigWorkerInfoTabFormData,
} from 'containers/jobsiteConfiguration/types';
import { getOnboardingSectionUpdateInput } from 'containers/jobsiteConfiguration/tabs/onboarding/onboardingSection';
import { Jobsite } from './types';
import {
  workerInfoSectionInputs,
  getWorkerInfoSectionDefaultValues,
  getWorkerInfoSectionUpdateInput,
} from './workerInfoSection';
import {
  getSiteSpecificOrientationSectionDefaultValues,
  getSiteSpecificOrientationSectionUpdateInput,
  siteSpecificOrientationSectionInputs,
} from './siteSpecificOrientationSection';

export type EditJobsiteConfigTabFormData = {
  workerInfo: EditJobsiteConfigWorkerInfoTabFormData;
  siteSpecificOrientation: EditJobsiteConfigurationFormData['siteSpecificOrientation'];
};

export const getFormInputs =
  (): TypedFormInputs<EditJobsiteConfigTabFormData> => {
    return {
      workerInfo: {
        element: FormInputTypes.Section,
        layout: '',
        elementProps: {
          title: 'Worker Info',
          details: 'Indicate which fields are required or visible',
        },
        children: workerInfoSectionInputs,
      },
      siteSpecificOrientation: {
        element: FormInputTypes.Section,
        layout: '',
        elementProps: {
          title: 'Site-specific Orientation',
          details: 'Indicate which fields are required',
        },
        children: siteSpecificOrientationSectionInputs,
      },
    };
  };

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
};

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const { jobsite } = args;

  const workerInfo = getWorkerInfoSectionDefaultValues(jobsite);
  const siteSpecificOrientation =
    getSiteSpecificOrientationSectionDefaultValues(jobsite);

  return { workerInfo, siteSpecificOrientation };
};

export const getOnboardingModuleInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput['onboardingModule'] => {
  const { personalInfoStep } = getWorkerInfoSectionUpdateInput(
    data.workerInfo,
    dirtyFields.workerInfo,
  );
  const { siteSpecificOrientationStep } =
    getSiteSpecificOrientationSectionUpdateInput(
      data.siteSpecificOrientation,
      dirtyFields.siteSpecificOrientation,
    );

  const fieldsByStepKey = {
    [OnboardingStepKey.SiteSpecificOrientation]:
      siteSpecificOrientationStep?.fields,
  };

  // This will upsert SiteSpecificOrientation step changed fields within the onboarding module.
  // At this moment, `stickerNumber` is the only SiteSpecificOrientation
  // field that exists in the WorkerInfo tab.
  const { steps: otherSteps } = getOnboardingSectionUpdateInput(
    {} as EditJobsiteConfigurationFormData['onboarding'],
    {},
    fieldsByStepKey,
  );

  const steps = [personalInfoStep, ...otherSteps].filter(Boolean);
  const hasChanged = Boolean(steps?.length);

  return hasChanged
    ? {
        name: `${jobsite.name} - onboarding`,
        slug: `${titleToKebabCase(jobsite.name)}-onboarding`,
        isEnabled: true,
        steps,
      }
    : undefined;
};

export const getJobsiteUpdateInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput => {
  const { jobsiteCustomWorkerDataFields } =
    getSiteSpecificOrientationSectionUpdateInput(
      data.siteSpecificOrientation,
      dirtyFields.siteSpecificOrientation,
    );

  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    onboardingModule: getOnboardingModuleInput(jobsite, data, dirtyFields),
    jobsiteCustomWorkerDataFields: jobsiteCustomWorkerDataFields?.length
      ? jobsiteCustomWorkerDataFields
      : undefined,
  });
};
